import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, Button, Table, useTable } from "@repo/o37-ui";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Edit2,
  CheckCircle,
  Play,
  Archive,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  Package,
  UserPlus,
  DollarSign
} from "lucide-react";
import {
  HoldingsCostPlan,
  HoldingsPlanLineItem,
  HoldingsSubscription,
  formatCents,
  HOLDINGS_PLAN_STATUSES
} from '../../types';
import PlanLineItemForm from '../../components/Holdings/PlanLineItemForm';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

export default function PlanDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [plan, setPlan] = useState<HoldingsCostPlan | null>(null);
  const [lineItems, setLineItems] = useState<HoldingsPlanLineItem[]>([]);
  const [subscriptions, setSubscriptions] = useState<HoldingsSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [showLineItemForm, setShowLineItemForm] = useState(false);
  const [editingLineItem, setEditingLineItem] = useState<HoldingsPlanLineItem | null>(null);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 });
  const [actionLoading, setActionLoading] = useState(false);

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    if (id) loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [planRes, subsRes] = await Promise.all([
        fetch(`${API_URL}/holdings/plans/${id}`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        }),
        fetch(`${API_URL}/holdings/subscriptions`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        })
      ]);

      if (planRes.ok) {
        const data = await planRes.json();
        setPlan({
          id: data.id,
          name: data.name,
          description: data.description,
          timeHorizonMonths: data.time_horizon_months,
          startDate: data.start_date,
          endDate: data.end_date,
          status: data.status,
          approvedBy: data.approved_by,
          approvedAt: data.approved_at,
          executedAt: data.executed_at,
          notes: data.notes,
          createdBy: data.created_by,
          createdAt: data.created_at,
          updatedAt: data.updated_at || data.created_at
        });

        if (data.line_items) {
          setLineItems(data.line_items.map((li: any) => ({
            id: li.id,
            planId: li.plan_id,
            itemType: li.item_type,
            name: li.name,
            description: li.description,
            subscriptionId: li.subscription_id,
            newCostCents: li.new_cost_cents,
            newBillingCycle: li.new_billing_cycle,
            seatChange: li.seat_change,
            personName: li.person_name,
            role: li.role,
            salaryCents: li.salary_cents,
            headcountDelta: li.headcount_delta,
            expenseDate: li.expense_date,
            expenseCategory: li.expense_category,
            companyId: li.company_id,
            effectiveDate: li.effective_date,
            monthlyImpactCents: li.monthly_impact_cents,
            annualImpactCents: li.annual_impact_cents,
            notes: li.notes,
            subscription: li.holdings_subscriptions ? {
              id: li.holdings_subscriptions.id,
              vendorName: li.holdings_subscriptions.vendor_name
            } : undefined
          })));
        }
      }

      if (subsRes.ok) {
        const data = await subsRes.json();
        setSubscriptions(data.map((s: any) => ({
          id: s.id,
          vendorName: s.vendor_name,
          costCents: s.cost_cents,
          billingCycle: s.billing_cycle,
          status: s.status
        })));
      }
    } catch (e) {
      console.error('Failed to load plan:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLineItem = async (lineItemId: string) => {
    if (!confirm('Delete this line item?')) return;

    try {
      const response = await fetch(`${API_URL}/holdings/plans/${id}/line-items/${lineItemId}`, {
        method: 'DELETE',
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        loadData();
      }
    } catch (e) {
      console.error('Failed to delete line item:', e);
    }
  };

  const handleApprove = async () => {
    if (!confirm('Approve this plan? This indicates the plan is ready for execution.')) return;

    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/holdings/plans/${id}/approve`, {
        method: 'POST',
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        loadData();
      }
    } catch (e) {
      console.error('Failed to approve plan:', e);
    } finally {
      setActionLoading(false);
    }
  };

  const handleExecute = async () => {
    if (!confirm('Execute this plan? This will apply all line items to your actual subscriptions and costs.')) return;

    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/holdings/plans/${id}/execute`, {
        method: 'POST',
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        loadData();
      }
    } catch (e) {
      console.error('Failed to execute plan:', e);
    } finally {
      setActionLoading(false);
    }
  };

  const handleArchive = async () => {
    if (!confirm('Archive this plan?')) return;

    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/holdings/plans/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        },
        body: JSON.stringify({ status: 'archived' })
      });
      if (response.ok) {
        loadData();
      }
    } catch (e) {
      console.error('Failed to archive plan:', e);
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getItemTypeIcon = (type: string) => {
    switch (type) {
      case 'new_subscription':
        return <Plus className="w-4 h-4 text-blue-500" />;
      case 'modify_subscription':
        return <Edit2 className="w-4 h-4 text-amber-500" />;
      case 'cancel_subscription':
        return <Trash2 className="w-4 h-4 text-red-500" />;
      case 'headcount_change':
        return <UserPlus className="w-4 h-4 text-purple-500" />;
      case 'one_time_expense':
        return <DollarSign className="w-4 h-4 text-emerald-500" />;
      default:
        return <Package className="w-4 h-4 text-neutral-500" />;
    }
  };

  const getItemTypeLabel = (type: string) => {
    switch (type) {
      case 'new_subscription': return 'New Subscription';
      case 'modify_subscription': return 'Modify Subscription';
      case 'cancel_subscription': return 'Cancel Subscription';
      case 'headcount_change': return 'Headcount Change';
      case 'one_time_expense': return 'One-Time Expense';
      default: return type;
    }
  };

  // Calculate totals
  const totalMonthlyImpact = lineItems.reduce((sum, li) => sum + li.monthlyImpactCents, 0);
  const totalAnnualImpact = lineItems.reduce((sum, li) => sum + li.annualImpactCents, 0);

  const columns = useMemo(() => [
    {
      id: "type",
      header: "Type",
      cell: ({ row }: { row: { original: HoldingsPlanLineItem } }) => (
        <div className="flex items-center gap-2">
          {getItemTypeIcon(row.original.itemType)}
          <span className="text-xs text-neutral-600">
            {getItemTypeLabel(row.original.itemType)}
          </span>
        </div>
      )
    },
    {
      id: "name",
      header: "Item",
      cell: ({ row }: { row: { original: HoldingsPlanLineItem } }) => (
        <div>
          <div className="font-medium text-neutral-900">{row.original.name}</div>
          {row.original.description && (
            <div className="text-sm text-neutral-500 truncate max-w-xs">
              {row.original.description}
            </div>
          )}
          {row.original.subscription && (
            <div className="text-xs text-blue-600">
              Linked: {row.original.subscription.vendorName}
            </div>
          )}
        </div>
      )
    },
    {
      id: "effectiveDate",
      header: "Effective Date",
      cell: ({ row }: { row: { original: HoldingsPlanLineItem } }) => (
        <span className="text-sm text-neutral-600">
          {formatDate(row.original.effectiveDate)}
        </span>
      )
    },
    {
      id: "monthlyImpact",
      header: "Monthly Impact",
      cell: ({ row }: { row: { original: HoldingsPlanLineItem } }) => {
        const impact = row.original.monthlyImpactCents;
        return (
          <div className="flex items-center gap-1.5">
            {impact > 0 ? (
              <TrendingUp className="w-4 h-4 text-red-500" />
            ) : impact < 0 ? (
              <TrendingDown className="w-4 h-4 text-emerald-500" />
            ) : null}
            <span className={`font-medium ${
              impact > 0 ? 'text-red-600' : impact < 0 ? 'text-emerald-600' : 'text-neutral-500'
            }`}>
              {impact > 0 ? '+' : ''}{formatCents(impact)}
            </span>
          </div>
        );
      }
    },
    {
      id: "annualImpact",
      header: "Annual Impact",
      cell: ({ row }: { row: { original: HoldingsPlanLineItem } }) => {
        const impact = row.original.annualImpactCents;
        return (
          <span className={`font-medium ${
            impact > 0 ? 'text-red-600' : impact < 0 ? 'text-emerald-600' : 'text-neutral-500'
          }`}>
            {impact > 0 ? '+' : ''}{formatCents(impact)}
          </span>
        );
      }
    },
    {
      id: "actions",
      header: "",
      size: 100,
      cell: ({ row }: { row: { original: HoldingsPlanLineItem } }) => (
        plan?.status === 'draft' ? (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="h-8 text-xs px-2"
              onClick={() => {
                setEditingLineItem(row.original);
                setShowLineItemForm(true);
              }}
              icon={<Edit2 className="w-3.5 h-3.5" />}
              text=""
            />
            <Button
              variant="outline"
              className="h-8 text-xs px-2 text-red-600 border-red-200 hover:bg-red-50"
              onClick={() => handleDeleteLineItem(row.original.id)}
              icon={<Trash2 className="w-3.5 h-3.5" />}
              text=""
            />
          </div>
        ) : null
      )
    }
  ], [plan?.status]);

  const { table, ...tableProps } = useTable({
    data: lineItems,
    columns,
    pagination,
    onPaginationChange: setPagination,
    rowCount: lineItems.length,
    resourceName: (plural) => `line item${plural ? "s" : ""}`,
  });

  if (loading) {
    return (
      <div>
        <h1 className="text-2xl font-semibold mb-2">Plan Details</h1>
        <p className="text-neutral-500">Loading...</p>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
        <h2 className="text-xl font-semibold mb-2">Plan Not Found</h2>
        <Button onClick={() => navigate('/holdings/planning')} text="Back to Plans" />
      </div>
    );
  }

  const statusConfig = HOLDINGS_PLAN_STATUSES.find(s => s.status === plan.status);

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/holdings/planning')}
          className="flex items-center gap-1.5 text-neutral-500 hover:text-neutral-700 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Plans</span>
        </button>

        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-semibold">{plan.name}</h1>
              <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusConfig?.color || 'bg-neutral-100 text-neutral-700'}`}>
                {statusConfig?.label || plan.status}
              </span>
            </div>
            {plan.description && (
              <p className="text-neutral-500 mb-2">{plan.description}</p>
            )}
            <p className="text-sm text-neutral-500">
              {plan.timeHorizonMonths} month horizon • {formatDate(plan.startDate)} to {plan.endDate ? formatDate(plan.endDate) : 'Ongoing'}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {plan.status === 'draft' && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setShowLineItemForm(true)}
                  icon={<Plus className="w-4 h-4" />}
                  text="Add Item"
                />
                <Button
                  onClick={handleApprove}
                  disabled={actionLoading || lineItems.length === 0}
                  icon={<CheckCircle className="w-4 h-4" />}
                  text="Approve"
                />
              </>
            )}
            {plan.status === 'approved' && (
              <>
                <Button
                  variant="outline"
                  onClick={handleArchive}
                  disabled={actionLoading}
                  icon={<Archive className="w-4 h-4" />}
                  text="Archive"
                />
                <Button
                  onClick={handleExecute}
                  disabled={actionLoading}
                  icon={<Play className="w-4 h-4" />}
                  text="Execute Plan"
                />
              </>
            )}
            {plan.status === 'executed' && (
              <Button
                variant="outline"
                onClick={handleArchive}
                disabled={actionLoading}
                icon={<Archive className="w-4 h-4" />}
                text="Archive"
              />
            )}
            <Button
              variant="outline"
              onClick={() => navigate(`/holdings/planning/${id}/compare`)}
              text="Compare to Current"
            />
          </div>
        </div>
      </div>

      {/* Impact Summary */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-500 mb-1">Total Monthly Impact</p>
                <p className={`text-2xl font-semibold ${
                  totalMonthlyImpact > 0 ? 'text-red-600' : totalMonthlyImpact < 0 ? 'text-emerald-600' : 'text-neutral-900'
                }`}>
                  {totalMonthlyImpact > 0 ? '+' : ''}{formatCents(totalMonthlyImpact)}
                </p>
              </div>
              {totalMonthlyImpact !== 0 && (
                totalMonthlyImpact > 0 ? (
                  <TrendingUp className="w-8 h-8 text-red-200" />
                ) : (
                  <TrendingDown className="w-8 h-8 text-emerald-200" />
                )
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-500 mb-1">Total Annual Impact</p>
                <p className={`text-2xl font-semibold ${
                  totalAnnualImpact > 0 ? 'text-red-600' : totalAnnualImpact < 0 ? 'text-emerald-600' : 'text-neutral-900'
                }`}>
                  {totalAnnualImpact > 0 ? '+' : ''}{formatCents(totalAnnualImpact)}
                </p>
              </div>
              {totalAnnualImpact !== 0 && (
                totalAnnualImpact > 0 ? (
                  <TrendingUp className="w-8 h-8 text-red-200" />
                ) : (
                  <TrendingDown className="w-8 h-8 text-emerald-200" />
                )
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Line Items Table */}
      <Card>
        <CardContent className="p-0">
          {lineItems.length === 0 ? (
            <div className="text-center py-12 text-neutral-500">
              <Package className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
              <h3 className="text-lg font-medium mb-2">No Line Items</h3>
              <p className="mb-6">Add line items to define the changes in this plan.</p>
              {plan.status === 'draft' && (
                <Button
                  onClick={() => setShowLineItemForm(true)}
                  icon={<Plus className="w-4 h-4" />}
                  text="Add Line Item"
                />
              )}
            </div>
          ) : (
            <Table {...tableProps} table={table} />
          )}
        </CardContent>
      </Card>

      {/* Approval/Execution Info */}
      {(plan.approvedAt || plan.executedAt) && (
        <Card className="mt-6">
          <CardContent className="p-6">
            <h3 className="font-semibold text-neutral-900 mb-3">History</h3>
            <div className="space-y-2 text-sm text-neutral-600">
              {plan.approvedAt && (
                <p>Approved on {formatDate(plan.approvedAt)}</p>
              )}
              {plan.executedAt && (
                <p>Executed on {formatDate(plan.executedAt)}</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Line Item Form Modal */}
      {showLineItemForm && (
        <PlanLineItemForm
          planId={id!}
          lineItem={editingLineItem}
          subscriptions={subscriptions}
          onClose={() => {
            setShowLineItemForm(false);
            setEditingLineItem(null);
          }}
          onSaved={() => {
            setShowLineItemForm(false);
            setEditingLineItem(null);
            loadData();
          }}
        />
      )}
    </div>
  );
}
